import { Button, LinearProgress } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';

import CheckIcon from '@/icons/CheckIcon.svg?react';
import { ExampleSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

export const SplitPickerPopoverContent = (props: {
  selectedExamples: ExampleSchema[];
  splits: string[];
  isMutating: boolean;
  newSplitModalOpen: boolean;
  setNewSplitModalOpen: (open: boolean) => void;
  onSelectSplit: (split: string, remove: boolean) => void;
}) => {
  const [allSplits, setAllSplits] = useState<string[]>([...props.splits]);

  const allSelectedSplits = useMemo(() => {
    // a split is selected if all the selectedExamples contain that split
    const selectedSplits = new Set<string>();
    props.splits.forEach((split) => {
      if (
        props.selectedExamples.every((example) =>
          (example.metadata?.dataset_split ?? ['base']).includes(split)
        )
      ) {
        selectedSplits.add(split);
      }
    });
    return Array.from(selectedSplits);
  }, [props.selectedExamples, props.splits]);

  useEffect(() => {
    setAllSplits((prev) => {
      const newSplits = props.splits.filter((split) => !prev.includes(split));
      return [...prev, ...newSplits];
    });
  }, [props.splits]);

  return (
    <div className="flex flex-col">
      {props.isMutating ? (
        <div className="flex h-2 flex-col pt-0.5">
          <LinearProgress />
        </div>
      ) : (
        <div className="h-2" />
      )}
      <div className="flex items-center justify-between p-4 pt-2">
        <div className="text-md font-bold">Add to Split</div>
        <Button
          size="sm"
          onClick={() => {
            props.setNewSplitModalOpen(true);
          }}
          startDecorator={<div>+</div>}
          variant="outlined"
          color="neutral"
        >
          New Split
        </Button>
      </div>
      <div className="flex max-h-[400px] flex-col gap-3 overflow-auto px-4 pb-4">
        {allSplits.map((split) => (
          <div
            className="flex cursor-pointer justify-between gap-2"
            key={split}
            onClick={() =>
              props.onSelectSplit(split, allSelectedSplits.includes(split))
            }
          >
            <div className="text-md line-clamp-1 overflow-hidden break-words font-medium">
              {split}
            </div>
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 flex-col items-center justify-center rounded-md border border-primary',
                allSelectedSplits.includes(split) &&
                  'border-brand bg-brand-tertiary'
              )}
            >
              {allSelectedSplits.includes(split) && (
                <CheckIcon
                  className="h-4 w-4 text-brand-green-400"
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
